import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import apiClient from "@/lib/api";
import toast from "react-hot-toast";

const inputClass =
  "w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none";

export default function MissionConversionModal({
  isOpen,
  onClose,
  applicationId,
  onConverted,
}) {
  const [loading, setLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successResult, setSuccessResult] = useState(null);

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    contract_type: "cdi",
    tarif_client: "",
    salaire_annuel: "",
    tjm_consultant: "",
  });

  useEffect(() => {
    if (!isOpen || !applicationId) return;
    setLoading(true);
    setError(null);
    setSuccessResult(null);
    apiClient
      .get(`/recruitment/applications/${applicationId}/mission-prefill`)
      .then((res) => {
        const data = res.data;
        setPrefill(data);
        setForm({
          start_date: data.start_date || "",
          end_date: data.end_date || "",
          contract_type: data.contract_type || "cdi",
          tarif_client: data.tarif_client != null ? String(data.tarif_client) : "",
          salaire_annuel: data.salaire_annuel != null ? String(data.salaire_annuel) : "",
          tjm_consultant: data.tjm_consultant != null ? String(data.tjm_consultant) : "",
        });
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Échec du chargement du pré-remplissage mission.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, applicationId]);

  if (!isOpen) return null;

  // Realtime missing fields validation
  const missing = [];
  if (!form.start_date) missing.push("Date de début");
  if (!form.tarif_client) missing.push("Tarif client (TJM)");
  if (form.contract_type === "cdi" && !form.salaire_annuel) missing.push("Salaire annuel brut");
  if (form.contract_type !== "cdi" && !form.tjm_consultant) missing.push("Coût consultant (TJM achat)");

  const handleConvert = async (e) => {
    e.preventDefault();
    if (missing.length > 0) {
      setError(`Informations manquantes obligatoires : ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        start_date: form.start_date,
        end_date: form.end_date || undefined,
        contract_type: form.contract_type,
        tarif_client: parseFloat(form.tarif_client),
        salaire_annuel: form.contract_type === "cdi" && form.salaire_annuel ? parseFloat(form.salaire_annuel) : undefined,
        tjm_consultant: form.contract_type !== "cdi" && form.tjm_consultant ? parseFloat(form.tjm_consultant) : undefined,
      };
      const res = await apiClient.post(
        `/recruitment/applications/${applicationId}/convert-to-mission`,
        payload
      );
      setSuccessResult(res.data);
      toast.success("Mission créée avec succès dans Pilotage !");
      if (onConverted) onConverted(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec de la conversion en mission Pilotage.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent className="max-w-2xl bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="text-lg font-bold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-400" />
          Conversion en Mission Pilotage
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-400">
          Création de la mission, du collaborateur et promotion du client dans le module Pilotage.
        </DialogDescription>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : successResult ? (
          <div className="space-y-4 pt-2">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 space-y-2 text-xs">
              <div className="flex items-center gap-2 font-bold text-sm">
                <CheckCircle2 className="h-5 w-5" />
                Mission créée avec succès dans Pilotage
              </div>
              <p className="text-slate-300">
                Identifiant Mission : <strong className="text-white font-mono">{successResult.mission_id}</strong>
              </p>
              <p className="text-slate-300">
                Collaborateur : <strong className="text-white font-mono">{successResult.collaborator_id}</strong>
              </p>
              <p className="text-slate-300">
                Client Pilotage : <strong className="text-white font-mono">{successResult.client_id}</strong>
              </p>
              <div className="p-3 rounded-lg bg-slate-900 border border-white/10 text-[11px] text-slate-400">
                Règle de gestion financière : les feuilles de temps restent vierges ; aucun CA réalisé fictif n&apos;est injecté.
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white"
              >
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleConvert} className="space-y-4 pt-2">
            {error && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Diagnostic des informations manquantes */}
            {missing.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 space-y-1">
                <div className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  Diagnostic : Champs obligatoires à compléter avant conversion
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {missing.map((field, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded bg-amber-500/20 text-[10px] font-semibold border border-amber-500/30"
                    >
                      {field} manquant
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Dossier overview */}
            <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10 text-xs">
              <div>
                <span className="text-slate-500 block">Candidat</span>
                <span className="font-semibold text-white">{prefill?.consultant_name}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Client</span>
                <span className="font-semibold text-white">
                  {prefill?.client_name}
                  {prefill?.company_type === "prospect" && (
                    <span className="ml-1.5 text-[10px] text-amber-400 font-normal">
                      (Sera promu client actif)
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Mission Form Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Type de contrat *
                </label>
                <select
                  value={form.contract_type}
                  onChange={(e) => setForm({ ...form, contract_type: e.target.value })}
                  className={inputClass}
                >
                  <option value="cdi">CDI</option>
                  <option value="freelance">Freelance / Prestataire</option>
                  <option value="portage">Portage salarial</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Date de démarrage *
                </label>
                <input
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Date de fin prévue (facultatif)
                </label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Tarif facturé au client (€/j) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="10"
                  required
                  placeholder="Ex: 650"
                  value={form.tarif_client}
                  onChange={(e) => setForm({ ...form, tarif_client: e.target.value })}
                  className={inputClass}
                />
              </div>

              {form.contract_type === "cdi" ? (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    Salaire annuel brut (€) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="500"
                    required
                    placeholder="Ex: 50000"
                    value={form.salaire_annuel}
                    onChange={(e) => setForm({ ...form, salaire_annuel: e.target.value })}
                    className={inputClass}
                  />
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    CJM calculé : (Salaire brut × 1.5) / 210 j
                  </span>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">
                    TJM Achat Consultant (€/j) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    required
                    placeholder="Ex: 450"
                    value={form.tjm_consultant}
                    onChange={(e) => setForm({ ...form, tjm_consultant: e.target.value })}
                    className={inputClass}
                  />
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    CJM direct consultant
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-md flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Conversion en cours...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-3.5 w-3.5" />
                    Créer la mission Pilotage
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
